import React, { useState, useEffect, useCallback, useRef } from 'react';
import kpisApi from '../services/kpis';
import usersApi from '../services/users';
import './KPIs.css';

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const label = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  if (diff === 0) return `Aujourd'hui — ${label}`;
  if (diff === -1) return `Hier — ${label}`;
  if (diff === 1) return `Demain — ${label}`;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function shiftDate(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function getWeekRange() {
  const now = new Date();
  const day = now.getDay() || 7;
  const mon = new Date(now);
  mon.setDate(now.getDate() - day + 1);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { start: mon.toISOString().slice(0, 10), end: sun.toISOString().slice(0, 10) };
}

function getMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  return { start, end };
}

// ─── Composant principal ────────────────────────────────────────────────────

export default function KPIs({ user, editMode, setHeaderAction }) {
  const [tab, setTab] = useState('my-day');
  const [teamAccess, setTeamAccess] = useState({ services: [], isSuperAdmin: false, loaded: false });

  useEffect(() => {
    kpisApi.getMyTeamAccess()
      .then(res => setTeamAccess({ ...res.data, loaded: true }))
      .catch(() => setTeamAccess({ services: [], isSuperAdmin: false, loaded: true }));
  }, []);

  const canViewTeam = teamAccess.isSuperAdmin || teamAccess.services.length > 0;

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'my-day' ? 'active' : ''} onClick={() => setTab('my-day')}>
          📅 Ma journée
        </button>
        {teamAccess.loaded && canViewTeam && (
          <button className={tab === 'team' ? 'active' : ''} onClick={() => setTab('team')}>
            👥 Suivi équipe
          </button>
        )}
        {user.role === 'super-admin' && (
          <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>
            ⚙️ Configuration<span className="tab-sa-lock" title="Visible par les super-admins uniquement">🔒</span>
          </button>
        )}
      </div>

      {tab === 'my-day' && <MyDayView user={user} />}
      {tab === 'team' && canViewTeam && <TeamView user={user} allowedServices={teamAccess.services} />}
      {tab === 'config' && user.role === 'super-admin' && <ConfigView user={user} />}
    </div>
  );
}

// ─── Saisie sans perte (2026-10-12) ─────────────────────────────────────────
// Signalé : des collaborateurs saisissaient, changeaient d'onglet ou
// d'application, revenaient… et n'avaient plus rien. La valeur ne partait
// qu'en QUITTANT le champ ; sur mobile, passer en arrière-plan ne le fait pas
// toujours, et un retour rapide rechargeait les anciennes valeurs avant la fin
// de l'enregistrement. Désormais :
//  1. enregistrement pendant la frappe (1 s après la dernière touche) ;
//  2. tout ce qui est en attente part dès que la page passe en arrière-plan,
//     par un envoi `keepalive` qui survit à la fermeture de la page ;
//  3. un brouillon reste sur l'appareil tant que le serveur n'a pas confirmé :
//     au retour, la valeur est réaffichée (« non enregistré ») et renvoyée.
const DELAI_AUTOSAVE_MS = 1000;
const cleBrouillons = (userId) => `kpi-brouillons:${userId}`;
function lireBrouillons(userId) {
  try { return JSON.parse(localStorage.getItem(cleBrouillons(userId))) || {}; } catch { return {}; }
}
function ecrireBrouillon(userId, date, defId, valeur) {
  try {
    const b = lireBrouillons(userId);
    if (valeur === null) {
      if (b[date]) { delete b[date][defId]; if (!Object.keys(b[date]).length) delete b[date]; }
    } else {
      b[date] = { ...(b[date] || {}), [defId]: valeur };
    }
    localStorage.setItem(cleBrouillons(userId), JSON.stringify(b));
  } catch { /* stockage indisponible (navigation privée…) : l'enregistrement direct reste actif */ }
}
// Envoi de secours quand la page disparaît : axios peut être interrompu,
// `fetch` avec `keepalive` termine la requête même page fermée.
// `concessionId` = concession choisie dans le menu (user.concession_id), transmise
// comme le fait apiClient (en-tête X-Concession-Id).
function envoyerKeepalive(payload, concessionId) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
    if (concessionId) headers['X-Concession-Id'] = String(concessionId);
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/kpis/entry`, {
      method: 'PUT', headers, body: JSON.stringify(payload), keepalive: true,
    }).catch(() => {});
  } catch { /* le brouillon local reste le filet de sécurité */ }
}

// ─── Ma journée ─────────────────────────────────────────────────────────────

function MyDayView({ user }) {
  const isAdmin = user.role === 'super-admin' || user.role === 'admin';
  const [date, setDate] = useState(todayStr());
  const [entries, setEntries] = useState([]);
  const [fonction, setFonction] = useState(null);
  const [fonctions, setFonctions] = useState([]);
  const [selectedFonction, setSelectedFonction] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saveError, setSaveError] = useState(null); // { label, message } pour toast
  const dateRef = useRef(date);
  dateRef.current = date;
  // File des saisies en attente : { [defId]: { entry, value, date } } + minuteries.
  const enAttente = useRef({});
  const minuteries = useRef({});
  const [brouillons, setBrouillons] = useState({}); // defId → valeur non confirmée
  const envoyerRef = useRef(null);

  // Charger la liste des fonctions pour les admins
  useEffect(() => {
    if (isAdmin) {
      kpisApi.getFonctions().then(res => {
        setFonctions(res.data);
        if (res.data.length > 0 && !selectedFonction) {
          setSelectedFonction(res.data[0].code);
        }
      }).catch(() => {});
    }
  }, [isAdmin]); // eslint-disable-line

  const loadDay = useCallback(async (d, fonc) => {
    setLoading(true);
    try {
      const res = await kpisApi.getMyDay(d, isAdmin ? fonc : undefined);
      const liste = res.data.entries || [];
      setEntries(liste);
      setFonction(res.data.fonction);
      // Brouillons non confirmés pour ce jour : réaffichés puis renvoyés. Ils
      // priment sur la valeur serveur (celle-ci peut être antérieure à la frappe).
      const b = lireBrouillons(user.id)[d] || {};
      const repris = {};
      for (const e of liste) {
        const defId = String(e.definition_id);
        if (b[defId] !== undefined && !e.is_auto) {
          repris[defId] = b[defId];
          enAttente.current[defId] = { entry: e, value: b[defId], date: d };
          clearTimeout(minuteries.current[defId]);
          minuteries.current[defId] = setTimeout(() => envoyerRef.current && envoyerRef.current(defId), 300);
        }
      }
      setBrouillons(repris);
    } catch (err) {
      console.error('KPI load error:', err);
    }
    setLoading(false);
  }, [isAdmin, user.id]);

  useEffect(() => {
    if (isAdmin && !selectedFonction) return; // attendre le chargement des fonctions
    loadDay(date, selectedFonction);
  }, [date, selectedFonction, loadDay, isAdmin]);

  const envoyer = useCallback(async (defId) => {
    clearTimeout(minuteries.current[defId]);
    const p = enAttente.current[defId];
    if (!p) return;
    delete enAttente.current[defId];
    const value = parseFloat(p.value) || 0;
    setSaving(s => ({ ...s, [defId]: 'saving' }));
    try {
      await kpisApi.saveEntry({ kpi_definition_id: Number(defId), date: p.date, value });
      // Une frappe plus récente a pu arriver pendant l'envoi : on ne l'efface pas.
      if (!enAttente.current[defId]) {
        ecrireBrouillon(user.id, p.date, defId, null);
        setBrouillons(b => { const n = { ...b }; delete n[defId]; return n; });
      }
      setEntries(prev => prev.map(e => String(e.definition_id) === String(defId) && p.date === dateRef.current ? { ...e, value } : e));
      setSaving(s => ({ ...s, [defId]: 'saved' }));
      setTimeout(() => setSaving(s => (s[defId] === 'saved' ? { ...s, [defId]: null } : s)), 1500);
    } catch (err) {
      console.error('KPI save error:', err);
      setSaving(s => ({ ...s, [defId]: 'error' }));
      // Le brouillon reste sur l'appareil : la valeur n'est pas perdue et sera
      // renvoyée au prochain passage sur la page.
      const apiMsg = err?.response?.data?.error || err?.response?.data?.message;
      const statusInfo = err?.response?.status ? ` (HTTP ${err.response.status})` : '';
      setSaveError({
        label: p.entry.label || `KPI #${defId}`,
        message: apiMsg
          ? `${apiMsg}${statusInfo}`
          : `Échec de la sauvegarde${statusInfo}. La valeur est gardée sur cet appareil et sera renvoyée.`,
      });
      setTimeout(() => setSaveError(null), 8000);
    }
  }, [user.id]);

  envoyerRef.current = envoyer;

  // Frappe : brouillon local immédiat + envoi 1 s après la dernière touche.
  const handleChange = useCallback((entry, rawValue) => {
    const defId = String(entry.definition_id);
    enAttente.current[defId] = { entry, value: rawValue, date: dateRef.current };
    ecrireBrouillon(user.id, dateRef.current, defId, rawValue);
    setBrouillons(b => ({ ...b, [defId]: rawValue }));
    clearTimeout(minuteries.current[defId]);
    minuteries.current[defId] = setTimeout(() => envoyer(defId), DELAI_AUTOSAVE_MS);
  }, [user.id, envoyer]);

  // Sortie du champ / Entrée : envoi immédiat de ce qui attend.
  const handleSave = useCallback((entry) => { envoyer(String(entry.definition_id)); }, [envoyer]);

  // Page en arrière-plan, fermée, ou changement d'onglet du CRM : tout part.
  const toutEnvoyerEnSecours = useCallback(() => {
    for (const [defId, p] of Object.entries(enAttente.current)) {
      clearTimeout(minuteries.current[defId]);
      envoyerKeepalive({ kpi_definition_id: Number(defId), date: p.date, value: parseFloat(p.value) || 0 }, user?.concession_id);
    }
    enAttente.current = {};
  }, [user?.concession_id]);
  useEffect(() => {
    const surVisibilite = () => { if (document.visibilityState === 'hidden') toutEnvoyerEnSecours(); };
    document.addEventListener('visibilitychange', surVisibilite);
    window.addEventListener('pagehide', toutEnvoyerEnSecours);
    return () => {
      document.removeEventListener('visibilitychange', surVisibilite);
      window.removeEventListener('pagehide', toutEnvoyerEnSecours);
      toutEnvoyerEnSecours(); // démontage (autre onglet du CRM, autre page)
    };
  }, [toutEnvoyerEnSecours]);

  if (!isAdmin && !fonction && !loading) {
    return (
      <div className="kpi-empty">
        <span className="kpi-empty-icon">📊</span>
        Aucun KPI configuré pour votre profil.<br />
        Demandez à un administrateur de définir votre fonction.
      </div>
    );
  }

  const manualEntries = entries.filter(e => !e.is_auto);
  const filledCount = manualEntries.filter(e => e.value !== null && e.value > 0).length;
  const totalManual = manualEntries.length;
  const progressPct = totalManual > 0 ? (filledCount / totalManual) * 100 : 0;

  return (
    <div>
      {/* Toast d'erreur de sauvegarde KPI */}
      {saveError && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 100,
          maxWidth: 420, padding: '12px 16px', borderRadius: 8,
          background: 'var(--color-error-light)',
          color: 'var(--color-error)',
          border: '1px solid var(--color-error)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          fontSize: 13,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            ⚠️ Échec d'enregistrement — {saveError.label}
          </div>
          <div>{saveError.message}</div>
        </div>
      )}

      {/* Chips de fonctions pour admins */}
      {isAdmin && fonctions.length > 0 && (
        <div className="kpi-fonction-chips">
          {fonctions.map(f => (
            <button
              key={f.code}
              className={`kpi-fonction-chip${selectedFonction === f.code ? ' active' : ''}`}
              onClick={() => setSelectedFonction(f.code)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Navigation date */}
      <div className="kpi-date-nav">
        <button onClick={() => setDate(d => shiftDate(d, -1))}>←</button>
        <span className="kpi-date-label">{formatDateLabel(date)}</span>
        <button onClick={() => setDate(d => shiftDate(d, 1))}>→</button>
        {date !== todayStr() && (
          <button className="kpi-date-today" onClick={() => setDate(todayStr())}>Aujourd'hui</button>
        )}
      </div>

      {/* Barre de progression */}
      {totalManual > 0 && (
        <div className="kpi-progress-wrap">
          <div className="kpi-progress-label">{filledCount}/{totalManual} KPIs renseignés</div>
          <div className="kpi-progress">
            <div className="kpi-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {/* Grille de cards */}
      {loading ? (
        <div className="kpi-empty" style={{ padding: '40px' }}>Chargement...</div>
      ) : entries.length === 0 ? (
        <div className="kpi-empty">
          <span className="kpi-empty-icon">📊</span>
          Aucun KPI configuré pour cette fonction.
        </div>
      ) : (
        <div className="kpi-grid">
          {entries.map(entry => (
            <KpiCard
              key={entry.definition_id}
              entry={entry}
              savingState={saving[entry.definition_id]}
              brouillon={brouillons[String(entry.definition_id)]}
              onChange={handleChange}
              onSave={handleSave}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Card KPI individuelle ──────────────────────────────────────────────────

function KpiCard({ entry, savingState, brouillon, onChange, onSave }) {
  const [localValue, setLocalValue] = useState('');

  // Valeur affichée : le brouillon non confirmé s'il existe, sinon celle du serveur.
  useEffect(() => {
    if (brouillon !== undefined) { setLocalValue(String(brouillon)); return; }
    setLocalValue(entry.value !== null && entry.value !== undefined ? String(entry.value) : '');
  }, [entry.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = () => { onSave(entry); };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  const inputClass = `kpi-input${savingState === 'saving' ? ' saving' : ''}${savingState === 'saved' ? ' saved' : ''}${savingState === 'error' ? ' erreur' : ''}`;

  const isInstantane = entry.agregation === 'instantane';

  return (
    <div className={`kpi-card${entry.is_auto ? ' auto' : ''}`}>
      <div className="kpi-card-label">
        {entry.is_auto && <span className="lock-icon">🔒</span>}
        {entry.label}
        {entry.unite && <span className="kpi-card-unite">({entry.unite})</span>}
      </div>

      {entry.is_auto ? (
        <div className={`kpi-auto-value${entry.value === null ? ' kpi-auto-null' : ''}`}>
          {entry.value !== null ? (
            <>
              {entry.unite === '€' ? entry.value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : entry.value}
              {entry.unite && entry.unite !== '€' ? ` ${entry.unite}` : ''}
              {entry.unite === '€' ? ' €' : ''}
            </>
          ) : '—'}
        </div>
      ) : (
        <input
          type="number"
          inputMode="numeric"
          className={inputClass}
          value={localValue}
          onChange={e => { setLocalValue(e.target.value); onChange(entry, e.target.value); }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="—"
          min="0"
        />
      )}

      {!entry.is_auto && savingState === 'error' && (
        <div className="kpi-non-enregistre" title="Valeur gardée sur cet appareil ; elle est renvoyée automatiquement à votre prochain passage">
          ⚠ Non enregistré — gardé sur cet appareil
        </div>
      )}

      {isInstantane && !entry.is_auto && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, fontStyle: 'italic' }}>
          État du jour — remplace la valeur précédente
        </div>
      )}
    </div>
  );
}

// ─── Cellule éditable dans Suivi équipe (saisie déléguée par un manager) ───

function EditableKpiCell({ value, unite, savingState, meta, onSave }) {
  const [localValue, setLocalValue] = useState(value > 0 ? String(value) : '');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setLocalValue(value > 0 ? String(value) : '');
  }, [value]);

  const commit = () => {
    setEditing(false);
    const newVal = parseFloat(localValue) || 0;
    if (newVal !== (value || 0)) onSave(localValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') {
      setLocalValue(value > 0 ? String(value) : '');
      e.target.blur();
    }
  };

  const hasValue = value !== undefined && value !== null && value > 0;
  const cellClass = `kpi-cell-editable${hasValue ? ' filled' : ' missing'}${savingState === 'saving' ? ' saving' : ''}${savingState === 'saved' ? ' saved' : ''}`;
  const display = hasValue ? (unite === '€' ? `${Math.round(value).toLocaleString('fr-FR')} €` : value) : '—';

  return (
    <td className={cellClass} style={{ textAlign: 'center', position: 'relative', cursor: 'text' }}>
      {editing ? (
        <input
          type="number"
          inputMode="numeric"
          autoFocus
          className="kpi-team-input"
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          min="0"
        />
      ) : (
        <span onClick={() => setEditing(true)} style={{ display: 'inline-block', width: '100%' }}>
          {display}
        </span>
      )}
      {meta && meta.modified_by_name && !editing && (
        <span className="kpi-cell-edited" title={`Modifié par ${meta.modified_by_name}${meta.updated_at ? ' le ' + new Date(meta.updated_at).toLocaleString('fr-FR') : ''}`}>✎</span>
      )}
    </td>
  );
}

// ─── Suivi équipe ───────────────────────────────────────────────────────────

const SERVICE_OPTIONS = ['ATELIER', 'AUTRE', 'PIECES', 'VN', 'VO'];

function TeamView({ user, allowedServices = [] }) {
  // Si l'utilisateur n'a qu'un seul service autorisé → on le pré-sélectionne et on cache le select
  const visibleServices = allowedServices.length === SERVICE_OPTIONS.length
    ? SERVICE_OPTIONS
    : allowedServices;
  // period : 'today' | 'week' | 'month' | 'range' (plage personnalisée)
  const [period, setPeriod] = useState('today');
  const [rangeStart, setRangeStart] = useState(todayStr());
  const [rangeEnd, setRangeEnd] = useState(todayStr());
  const [fonctionFilter, setFonctionFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  // Concession : celle choisie dans le menu (la page est remontée à chaque changement).
  const [concessionFilter] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [fonctions, setFonctions] = useState([]);
  const [teamData, setTeamData] = useState({ users: [], definitions: [], editableServices: [] });
  const [loading, setLoading] = useState(true);
  // État de sauvegarde par cellule : `${userId}:${defId}` → 'saving' | 'saved' | null
  const [cellSaving, setCellSaving] = useState({});

  useEffect(() => {
    kpisApi.getFonctions().then(res => setFonctions(res.data)).catch(() => {});
  }, []);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    let start, end;
    if (period === 'today') {
      start = end = todayStr();
    } else if (period === 'yesterday') {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      start = end = y.toISOString().slice(0, 10);
    } else if (period === 'week') {
      ({ start, end } = getWeekRange());
    } else if (period === 'month') {
      ({ start, end } = getMonthRange());
    } else if (period === 'range') {
      start = rangeStart;
      end = rangeEnd;
      // Sécurité : si start > end, on inverse pour éviter une plage vide.
      if (start && end && start > end) { const t = start; start = end; end = t; }
    } else {
      start = end = todayStr();
    }

    try {
      const res = await kpisApi.getTeam({
        start,
        end,
        fonction: fonctionFilter || undefined,
        service: serviceFilter || undefined,
        concession_id: concessionFilter || undefined,
      });
      setTeamData(res.data);
    } catch (err) {
      console.error('KPI team error:', err);
    }
    setLoading(false);
  }, [period, rangeStart, rangeEnd, fonctionFilter, serviceFilter, concessionFilter]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const exportCSV = () => {
    const { users: teamUsers, definitions } = teamData;
    if (!teamUsers.length) return;

    const headers = ['Collaborateur', 'Fonction', 'Service', ...definitions.map(d => d.label)];
    const rows = teamUsers.map(u => {
      const cols = [
        `${u.nom} ${u.prenom}`,
        u.fonction || '',
        u.service_concession || '',
        ...definitions.map(d => {
          const val = u.entries[d.id];
          return val !== undefined ? val : '';
        }),
      ];
      return cols.map(c => `"${c}"`).join(';');
    });

    const csv = [headers.map(h => `"${h}"`).join(';'), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kpis_equipe_${todayStr()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filtrer les définitions pour ne montrer que celles pertinentes aux fonctions affichées.
  // d.fonctions[] = liste des fonctions concernées par le KPI. '*' = universel.
  // Rétrocompat : si fonctions[] absent, on retombe sur l'ancien champ scalaire fonction.
  const userFonctions = [...new Set(teamData.users.map(u => (u.fonction || '').toLowerCase()).filter(Boolean))];
  const getDefFonctions = (d) => d.fonctions?.length ? d.fonctions : (d.fonction ? [d.fonction] : []);
  const relevantDefs = teamData.definitions.filter(d => {
    const fons = getDefFonctions(d);
    return fons.includes('*') || fons.some(f => userFonctions.includes(f));
  });

  // Totaux par colonne — réactifs aux filtres (period / fonction / service) car
  // teamData est rechargé à chaque changement de filtre.
  const totals = relevantDefs.map(d => {
    const defFonctions = getDefFonctions(d);
    let sum = 0;
    for (const u of teamData.users) {
      const uFonction = (u.fonction || '').toLowerCase();
      if (!defFonctions.includes('*') && !defFonctions.includes(uFonction)) continue;
      const v = u.entries[d.id];
      if (typeof v === 'number' && !isNaN(v)) sum += v;
    }
    return sum;
  });
  const formatTotal = (v, unite) => {
    if (unite === '€') return `${Math.round(v).toLocaleString('fr-FR')} €`;
    if (Number.isInteger(v)) return v.toLocaleString('fr-FR');
    return v.toFixed(1);
  };

  // Édition déléguée : sauvegarde la valeur saisie pour `target_user_id` sur la date du jour.
  // Disponible uniquement quand la vue représente UNE seule journée et que cette
  // journée est aujourd'hui (sinon on agrège ou on regarde le passé).
  const editableServices = teamData.editableServices || [];
  const today = todayStr();
  const isSingleTodayView =
    period === 'today' ||
    (period === 'range' && rangeStart === today && rangeEnd === today);
  const canEditInline = isSingleTodayView && editableServices.length > 0;
  const handleCellSave = async (targetUser, def, rawValue) => {
    const value = parseFloat(rawValue) || 0;
    const cellKey = `${targetUser.id}:${def.id}`;
    setCellSaving(s => ({ ...s, [cellKey]: 'saving' }));
    try {
      await kpisApi.saveEntry({
        kpi_definition_id: def.id,
        date: todayStr(),
        value,
        target_user_id: targetUser.id,
      });
      // Mise à jour optimiste : on remplace la valeur dans teamData
      setTeamData(td => ({
        ...td,
        users: td.users.map(u => {
          if (u.id !== targetUser.id) return u;
          return {
            ...u,
            entries: { ...u.entries, [def.id]: value },
            meta: {
              ...(u.meta || {}),
              [def.id]: {
                modified_by_name: `${user.prenom || ''} ${user.nom || ''}`.trim(),
                updated_at: new Date().toISOString(),
              },
            },
          };
        }),
      }));
      setCellSaving(s => ({ ...s, [cellKey]: 'saved' }));
      setTimeout(() => setCellSaving(s => ({ ...s, [cellKey]: null })), 1500);
    } catch (err) {
      console.error('KPI delegated save error:', err);
      const msg = err.response?.data?.error || 'Erreur lors de la sauvegarde';
      alert(msg);
      setCellSaving(s => ({ ...s, [cellKey]: null }));
    }
  };

  return (
    <div>
      <div className="kpi-team-header">
        <div className="kpi-period-tabs">
          {[
            { key: 'today', label: "Aujourd'hui" },
            { key: 'yesterday', label: 'Hier' },
            { key: 'week', label: 'Cette semaine' },
            { key: 'month', label: 'Ce mois' },
            { key: 'range', label: 'Plage personnalisée' },
          ].map(p => (
            <button
              key={p.key}
              className={`kpi-period-tab${period === p.key ? ' active' : ''}`}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {period === 'range' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="date"
              value={rangeStart}
              max={todayStr()}
              onChange={e => setRangeStart(e.target.value || todayStr())}
              style={{ height: 36 }}
            />
            <span style={{ color: 'var(--text-secondary)' }}>→</span>
            <input
              type="date"
              value={rangeEnd}
              max={todayStr()}
              onChange={e => setRangeEnd(e.target.value || todayStr())}
              style={{ height: 36 }}
            />
          </div>
        )}

        <select value={fonctionFilter} onChange={e => setFonctionFilter(e.target.value)}>
          <option value="">Toutes les fonctions</option>
          {fonctions.map(f => (
            <option key={f.code} value={f.code}>{f.label}</option>
          ))}
        </select>

        {visibleServices.length > 1 && (
          <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}>
            <option value="">{visibleServices.length === SERVICE_OPTIONS.length ? 'Tous les services' : `Tous mes services (${visibleServices.join(', ')})`}</option>
            {visibleServices.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}

        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}

        <button className="kpi-team-export" onClick={exportCSV}>📥 Export CSV</button>
      </div>

      {loading ? (
        <div className="kpi-empty" style={{ padding: '40px' }}>Chargement...</div>
      ) : !teamData.users.length ? (
        <div className="kpi-empty">
          <span className="kpi-empty-icon">👥</span>
          Aucun collaborateur trouvé.
        </div>
      ) : (
        <div className="table-container kpi-team-table-wrap">
          <table className="kpi-team-table">
            <thead>
              <tr>
                <th>Collaborateur</th>
                {relevantDefs.map(d => (
                  <th key={d.id} title={d.label}>{d.label}{d.unite ? ` (${d.unite})` : ''}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teamData.users.map(u => {
                const uFonction = (u.fonction || '').toLowerCase();
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }} title={`${u.fonction || '—'} · ${u.service_concession || '—'}`}>{u.nom} {u.prenom}</td>
                    {relevantDefs.map(d => {
                      // Ce KPI s'applique-t-il à cet utilisateur ?
                      const defFonctions = getDefFonctions(d);
                      const applies = defFonctions.includes('*') || defFonctions.includes(uFonction);
                      if (!applies) {
                        return <td key={d.id} className="kpi-cell-na">—</td>;
                      }
                      const val = u.entries[d.id];
                      const isAuto = d.type === 'auto';
                      const hasValue = val !== undefined && val !== null && val > 0;
                      const cellClass = isAuto ? 'kpi-cell-auto' : (hasValue ? 'kpi-cell-filled' : 'kpi-cell-missing');
                      const meta = (u.meta || {})[d.id];
                      const isEditable = canEditInline && !isAuto && editableServices.includes(u.service_concession);
                      if (isEditable) {
                        return (
                          <EditableKpiCell
                            key={d.id}
                            value={val || 0}
                            unite={d.unite}
                            savingState={cellSaving[`${u.id}:${d.id}`]}
                            meta={meta}
                            onSave={(rawValue) => handleCellSave(u, d, rawValue)}
                          />
                        );
                      }
                      return (
                        <td key={d.id} className={cellClass} style={{ textAlign: 'center', position: 'relative' }}>
                          {hasValue ? (d.unite === '€' ? `${Math.round(val).toLocaleString('fr-FR')} €` : val) : (isAuto ? '0' : '—')}
                          {meta && meta.modified_by_name && (
                            <span className="kpi-cell-edited" title={`Modifié par ${meta.modified_by_name}${meta.updated_at ? ' le ' + new Date(meta.updated_at).toLocaleString('fr-FR') : ''}`}>✎</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="kpi-team-total-row">
                <td>Total ({teamData.users.length} collab.)</td>
                {relevantDefs.map((d, i) => (
                  <td key={d.id} style={{ textAlign: 'center' }}>
                    {totals[i] > 0 ? formatTotal(totals[i], d.unite) : '—'}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Configuration (super-admin) ────────────────────────────────────────────

function ConfigView({ user }) {
  const [definitions, setDefinitions] = useState([]);
  const [fonctions, setFonctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDef, setEditingDef] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defsRes, foncRes] = await Promise.all([
        kpisApi.getDefinitions(),
        kpisApi.getFonctions(),
      ]);
      setDefinitions(defsRes.data);
      setFonctions(foncRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Désactiver ce KPI ?')) return;
    try {
      await kpisApi.deleteDefinition(id);
      setDefinitions(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (def) => {
    setEditingDef(def);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingDef(null);
  };

  const handleFormSave = async (formData) => {
    try {
      if (editingDef) {
        await kpisApi.updateDefinition(editingDef.id, formData);
      } else {
        await kpisApi.createDefinition(formData);
      }
      handleFormClose();
      load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  // Grouper par fonction : un KPI multi-fonctions apparaît dans chacun des groupes
  // concernés. '*' tombe dans la section spéciale « Toutes les fonctions ».
  // Rétrocompat : si fonctions[] vide, on utilise l'ancien champ scalaire fonction.
  const grouped = {};
  for (const d of definitions) {
    const fons = d.fonctions?.length ? d.fonctions : (d.fonction ? [d.fonction] : []);
    for (const f of fons) {
      if (!grouped[f]) grouped[f] = [];
      grouped[f].push(d);
    }
  }
  const fonctionLabel = (code) => code === '*' ? 'Toutes les fonctions' : (fonctions.find(f => f.code === code)?.label || code);

  return (
    <div>
      <TeamViewersManager />

      <h3 style={{ fontSize: 16, color: 'var(--text-primary)', margin: '32px 0 12px', borderTop: '1px solid var(--border-default)', paddingTop: 24 }}>
        Définitions des KPIs
      </h3>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{definitions.length} KPIs configurés</span>
        <button
          className="kpi-team-export"
          style={{ marginLeft: 0 }}
          onClick={() => { setEditingDef(null); setShowForm(true); }}
        >
          + Ajouter un KPI
        </button>
      </div>

      {loading ? (
        <div className="kpi-empty" style={{ padding: '40px' }}>Chargement...</div>
      ) : (
        Object.keys(grouped).sort().map(fonc => (
          <div key={fonc} style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 10 }}>
              {fonctionLabel(fonc)}
            </h4>
            <div className="kpi-config-grid">
              {grouped[fonc].map(d => {
                const allFons = d.fonctions?.length ? d.fonctions : (d.fonction ? [d.fonction] : []);
                const isMulti = allFons.length > 1 && !allFons.includes('*');
                return (
                  <div className="kpi-config-row" key={`${fonc}-${d.id}`}>
                    <span className={`badge ${d.type}`}>{d.type === 'auto' ? 'Auto' : 'Manuel'}</span>
                    <span className="kpi-config-label">
                      {d.label}
                      {isMulti && (
                        <span title={`KPI partagé : ${allFons.map(fonctionLabel).join(', ')}`} style={{
                          marginLeft: 6, padding: '1px 6px', fontSize: 10, borderRadius: 8,
                          background: 'var(--color-info-light)', color: 'light-dark(var(--color-info), var(--color-info-dark))',
                          border: '1px solid var(--color-info)', fontWeight: 600, letterSpacing: 0.3,
                        }}>
                          ×{allFons.length} fonctions
                        </span>
                      )}
                    </span>
                    {d.unite && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{d.unite}</span>}
                    <span className="kpi-config-fonction">{d.code}</span>
                    <div className="kpi-config-actions">
                      <button onClick={() => handleEdit(d)}>Modifier</button>
                      <button className="delete" onClick={() => handleDelete(d.id)}>Supprimer</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {showForm && (
        <KpiDefinitionForm
          initial={editingDef}
          fonctions={fonctions}
          onSave={handleFormSave}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// ─── Formulaire ajout/modification KPI ──────────────────────────────────────

function KpiDefinitionForm({ initial, fonctions, onSave, onClose }) {
  // fonctions[] : array de codes fonction concernées par ce KPI. ['*'] = toutes.
  // Migration depuis l'ancien champ scalaire `fonction` si fonctions[] absent.
  const initialFonctions = initial?.fonctions?.length
    ? initial.fonctions
    : (initial?.fonction ? [initial.fonction] : []);
  const [form, setForm] = useState({
    code: initial?.code || '',
    label: initial?.label || '',
    fonctions: initialFonctions,
    type: initial?.type || 'manual',
    unite: initial?.unite || '',
    sort_order: initial?.sort_order || 0,
    agregation: initial?.agregation || 'cumul',
  });
  const [fonctionsOpen, setFonctionsOpen] = useState(false);
  const fonctionsDropdownRef = useRef(null);

  // Ferme le dropdown au clic en dehors
  useEffect(() => {
    if (!fonctionsOpen) return;
    const onClick = (e) => {
      if (fonctionsDropdownRef.current && !fonctionsDropdownRef.current.contains(e.target)) {
        setFonctionsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [fonctionsOpen]);

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const isAllFonctions = form.fonctions.includes('*');
  const toggleFonction = (code, checked) => {
    setForm(f => {
      const set = new Set(f.fonctions);
      if (checked) set.add(code); else set.delete(code);
      // Si '*' était présent et qu'on clique sur une fonction individuelle, on
      // bascule en mode liste explicite (retire '*').
      if (checked && code !== '*' && set.has('*')) set.delete('*');
      return { ...f, fonctions: Array.from(set) };
    });
  };
  const toggleAll = (checked) => {
    setForm(f => ({ ...f, fonctions: checked ? ['*'] : [] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.code || !form.label || form.fonctions.length === 0) return;
    onSave(form);
  };

  return (
    <div className="kpi-form-overlay" onClick={onClose}>
      <div className="kpi-form-modal" onClick={e => e.stopPropagation()}>
        <h3>{initial ? 'Modifier le KPI' : 'Nouveau KPI'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="kpi-form-field">
            <label>Code technique</label>
            <input
              value={form.code}
              onChange={e => handleChange('code', e.target.value)}
              placeholder="commercial_visites"
              disabled={!!initial}
            />
          </div>
          <div className="kpi-form-field">
            <label>Libellé</label>
            <input
              value={form.label}
              onChange={e => handleChange('label', e.target.value)}
              placeholder="Visites en concession"
            />
          </div>
          <div className="kpi-form-field">
            <label>Fonctions concernées <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(au moins une)</span></label>
            <div ref={fonctionsDropdownRef} style={{ position: 'relative' }}>
              {/* Bouton synthèse — comportement select natif */}
              <button
                type="button"
                onClick={() => setFonctionsOpen(o => !o)}
                style={{
                  width: '100%', padding: '8px 10px', textAlign: 'left',
                  border: '1px solid var(--border-default)', borderRadius: 6,
                  background: 'var(--bg-input)', color: 'var(--text-primary)',
                  cursor: 'pointer', fontSize: 13,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isAllFonctions
                    ? 'Toutes les fonctions'
                    : form.fonctions.length === 0
                      ? <span style={{ color: 'var(--text-muted)' }}>Sélectionnez…</span>
                      : form.fonctions.length === 1
                        ? (fonctions.find(f => f.code === form.fonctions[0])?.label || form.fonctions[0])
                        : `${form.fonctions.length} fonctions sélectionnées`
                  }
                </span>
                <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>▾</span>
              </button>
              {/* Panneau dropdown */}
              {fonctionsOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
                  maxHeight: 280, overflowY: 'auto',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)', borderRadius: 6,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  padding: 4,
                }}>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', fontSize: 13, cursor: 'pointer',
                    borderRadius: 4,
                    background: isAllFonctions ? 'var(--color-info-light)' : 'transparent',
                  }}>
                    <input
                      type="checkbox"
                      checked={isAllFonctions}
                      onChange={(e) => toggleAll(e.target.checked)}
                      style={{ width: 16, height: 16, margin: 0, padding: 0, flexShrink: 0 }}
                    />
                    <strong>Toutes les fonctions</strong>
                  </label>
                  <div style={{ borderTop: '1px dashed var(--border-default)', margin: '4px 0' }} />
                  {fonctions.map(f => (
                    <label
                      key={f.code}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 10px', fontSize: 13,
                        borderRadius: 4,
                        cursor: isAllFonctions ? 'not-allowed' : 'pointer',
                        opacity: isAllFonctions ? 0.45 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!isAllFonctions && form.fonctions.includes(f.code)}
                        disabled={isAllFonctions}
                        onChange={(e) => toggleFonction(f.code, e.target.checked)}
                        style={{ width: 16, height: 16, margin: 0, padding: 0, flexShrink: 0 }}
                      />
                      <span>{f.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="kpi-form-field" style={{ flex: 1 }}>
              <label>Type</label>
              <select value={form.type} onChange={e => handleChange('type', e.target.value)}>
                <option value="manual">Manuel</option>
                <option value="auto">Automatique</option>
              </select>
            </div>
            <div className="kpi-form-field" style={{ flex: 1 }}>
              <label>Unité</label>
              <input
                value={form.unite}
                onChange={e => handleChange('unite', e.target.value)}
                placeholder="€, %, ..."
              />
            </div>
          </div>
          <div className="kpi-form-field">
            <label>Ordre d'affichage</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={e => handleChange('sort_order', parseInt(e.target.value) || 0)}
            />
          </div>
          <div className="kpi-form-field">
            <label>Agrégation sur une période</label>
            <select value={form.agregation} onChange={e => handleChange('agregation', e.target.value)}>
              <option value="cumul">Cumul (somme des saisies)</option>
              <option value="instantane">Instantané (dernière valeur saisie)</option>
            </select>
            <small style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 4, display: 'block' }}>
              « Instantané » : la donnée est déjà cumulée par le collaborateur (ex. livraisons restantes du mois) — on affiche la dernière saisie au lieu de sommer.
            </small>
          </div>
          <div className="kpi-form-actions">
            <button type="button" onClick={onClose}>Annuler</button>
            <button type="submit" className="primary">{initial ? 'Enregistrer' : 'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Team Viewers Manager (super-admin) ─────────────────────────────────────

function TeamViewersManager() {
  const [viewers, setViewers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, user_id, services }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [viewersRes, usersRes] = await Promise.all([
        kpisApi.getTeamViewers(),
        usersApi.getAll(),
      ]);
      setViewers(viewersRes.data);
      const usersList = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []);
      setUsers(usersList.filter(u => u.actif !== false));
    } catch (err) {
      console.error('Team viewers load error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async ({ user_id, services, can_edit, id }) => {
    try {
      if (id) {
        await kpisApi.updateTeamViewer(id, { services, can_edit });
      } else {
        await kpisApi.addTeamViewer({ user_id, services, can_edit });
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Retirer ${label} de la liste des accès au Suivi équipe ?`)) return;
    try {
      await kpisApi.deleteTeamViewer(id);
      setViewers(prev => prev.filter(v => v.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const usedUserIds = new Set(viewers.map(v => v.user_id));
  const availableUsers = users.filter(u => !usedUserIds.has(u.id));

  return (
    <div>
      <h3 style={{ fontSize: 16, color: 'var(--text-primary)', margin: '0 0 8px' }}>
        Accès au Suivi équipe
      </h3>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 16px' }}>
        Liste des utilisateurs autorisés à consulter l'onglet « Suivi équipe » et les services qu'ils peuvent voir.
        Le super-administrateur a accès à tous les services par défaut (non listé ici).
      </p>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          className="kpi-team-export"
          style={{ marginLeft: 0 }}
          onClick={() => { setEditing({ user_id: '', services: [] }); setShowForm(true); }}
          disabled={!availableUsers.length}
          title={!availableUsers.length ? 'Tous les utilisateurs sont déjà listés' : ''}
        >
          + Ajouter un accès
        </button>
      </div>

      {loading ? (
        <div className="kpi-empty" style={{ padding: '20px' }}>Chargement...</div>
      ) : !viewers.length ? (
        <div className="kpi-empty">
          <span className="kpi-empty-icon">👥</span>
          Aucun utilisateur n'a encore accès au Suivi équipe.
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Rôle</th>
                <th>Fonction / Service</th>
                <th>Services autorisés</th>
                <th>Mode</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {viewers.map(v => (
                <tr key={v.id}>
                  <td style={{ fontWeight: 600 }}>{v.nom} {v.prenom}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{v.role}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {v.fonction || '—'}{v.service_concession ? ` · ${v.service_concession}` : ''}
                  </td>
                  <td>
                    {(v.services || []).map(s => (
                      <span key={s} className="badge manual" style={{ marginRight: 4 }}>{s}</span>
                    ))}
                  </td>
                  <td>
                    {v.can_edit
                      ? <span className="badge kpi-mode-edit">✏️ Édition</span>
                      : <span className="badge kpi-mode-view">👁️ Lecture</span>}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Modifier" onClick={() => { setEditing({ id: v.id, user_id: v.user_id, services: v.services || [], can_edit: !!v.can_edit, _label: `${v.nom} ${v.prenom}` }); setShowForm(true); }}>✏️</button>
                      <button className="btn-icon delete" title="Retirer" onClick={() => handleDelete(v.id, `${v.nom} ${v.prenom}`)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <TeamViewerForm
          initial={editing}
          users={editing?.id ? users : availableUsers}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function TeamViewerForm({ initial, users, onSave, onClose }) {
  const [userId, setUserId] = useState(initial?.user_id || '');
  const [services, setServices] = useState(initial?.services || []);
  const [canEdit, setCanEdit] = useState(initial?.can_edit || false);

  const toggleService = (s) => {
    setServices(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userId) return alert('Choisissez un utilisateur');
    if (!services.length) return alert('Sélectionnez au moins un service');
    onSave({ id: initial?.id, user_id: userId, services, can_edit: canEdit });
  };

  const isEditing = !!initial?.id;

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Modifier l\'accès' : 'Nouvel accès Suivi équipe'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ textAlign: 'left' }}>
            <div className="kpi-form-field">
              <label>Utilisateur</label>
              {isEditing ? (
                <input value={initial._label || ''} disabled />
              ) : (
                <select value={userId} onChange={e => setUserId(parseInt(e.target.value) || '')} required>
                  <option value="">— Sélectionner —</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.nom} {u.prenom} {u.fonction ? `· ${u.fonction}` : ''}{u.service_concession ? ` · ${u.service_concession}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="kpi-form-field">
              <label style={{ display: 'block', textAlign: 'left' }}>Services autorisés</label>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginTop: 6 }}>
                {SERVICE_OPTIONS.map(s => (
                  <label
                    key={s}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      margin: 0,
                      fontWeight: 400,
                      fontSize: 14,
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <input type="checkbox" checked={services.includes(s)} onChange={() => toggleService(s)} style={{ width: 16, height: 16, margin: 0, cursor: 'pointer' }} />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="kpi-form-field">
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  margin: 0,
                  fontWeight: 400,
                  fontSize: 14,
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <input
                  type="checkbox"
                  checked={canEdit}
                  onChange={() => setCanEdit(v => !v)}
                  style={{ width: 16, height: 16, margin: '2px 0 0', cursor: 'pointer' }}
                />
                <span>
                  <strong>Peut éditer les KPIs</strong> de ces services (correction des saisies des collaborateurs, fenêtre 30 jours).<br />
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Décoché = accès en lecture seule.</span>
                </span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-primary">{isEditing ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
